// Enhanced Token Manager for handling token expiration
class TokenManager {
  constructor() {
    this.TOKEN_KEY = 'access_token';
    this.REFRESH_TOKEN_KEY = 'refresh_token';
    this.EXPIRES_AT_KEY = 'token_expires_at';
    this.EXPIRES_IN_KEY = 'token_expires_in';
    
    // Set up automatic cleanup of expired tokens
    this.setupTokenValidation();
  }

  // Enhanced token setting with expiration tracking
  setTokens(accessToken, refreshToken, expiresAt = null, expiresIn = null) {
    try {
      sessionStorage.setItem(this.TOKEN_KEY, accessToken);
      sessionStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
      
      if (expiresAt) {
        sessionStorage.setItem(this.EXPIRES_AT_KEY, expiresAt);
      }
      
      if (expiresIn) {
        const expirationTime = new Date(Date.now() + (expiresIn * 1000));
        sessionStorage.setItem(this.EXPIRES_AT_KEY, expirationTime.toISOString());
        sessionStorage.setItem(this.EXPIRES_IN_KEY, expiresIn.toString());
      }
      
      console.log('Tokens set successfully with expiration:', {
        hasAccess: !!accessToken,
        hasRefresh: !!refreshToken,
        expiresAt: sessionStorage.getItem(this.EXPIRES_AT_KEY)
      });
      
      // Setup auto-logout timer
      this.setupAutoLogout();
      
    } catch (error) {
      console.error('Failed to set tokens:', error);
    }
  }

  getAccessToken() {
    try {
      const token = sessionStorage.getItem(this.TOKEN_KEY);
      
      // Check if token is expired
      if (token && this.isTokenExpired()) {
        console.log('Access token expired, clearing tokens');
        this.clearTokens();
        return null;
      }
      
      return token;
    } catch (error) {
      console.error('Failed to get access token:', error);
      return null;
    }
  }

  getRefreshToken() {
    try {
      return sessionStorage.getItem(this.REFRESH_TOKEN_KEY);
    } catch (error) {
      console.error('Failed to get refresh token:', error);
      return null;
    }
  }

  // Enhanced token expiration check
  isTokenExpired() {
    try {
      const expiresAt = sessionStorage.getItem(this.EXPIRES_AT_KEY);
      if (!expiresAt) {
        // If no expiration info, assume token is valid but set a default expiration
        console.warn('No token expiration info found, assuming valid for 2 hours');
        const defaultExpiration = new Date(Date.now() + (2 * 60 * 60 * 1000));
        sessionStorage.setItem(this.EXPIRES_AT_KEY, defaultExpiration.toISOString());
        return false;
      }
      
      const expirationDate = new Date(expiresAt);
      const now = new Date();
      const isExpired = now >= expirationDate;
      
      if (isExpired) {
        console.log('Token expired:', {
          now: now.toISOString(),
          expires: expirationDate.toISOString()
        });
      }
      
      return isExpired;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      // If we can't check expiration, assume expired for security
      return true;
    }
  }

  // Get time remaining until token expires (in seconds)
  getTimeUntilExpiration() {
    try {
      const expiresAt = sessionStorage.getItem(this.EXPIRES_AT_KEY);
      if (!expiresAt) return 0;
      
      const expirationDate = new Date(expiresAt);
      const now = new Date();
      const timeRemaining = Math.max(0, Math.floor((expirationDate - now) / 1000));
      
      return timeRemaining;
    } catch (error) {
      console.error('Error calculating time until expiration:', error);
      return 0;
    }
  }

  // Complete token cleanup
  clearTokens() {
    try {
      sessionStorage.removeItem(this.TOKEN_KEY);
      sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
      sessionStorage.removeItem(this.EXPIRES_AT_KEY);
      sessionStorage.removeItem(this.EXPIRES_IN_KEY);
      
      // Clear auto-logout timer
      if (this.logoutTimer) {
        clearTimeout(this.logoutTimer);
        this.logoutTimer = null;
      }
      
      // Clear validation interval
      if (this.validationInterval) {
        clearInterval(this.validationInterval);
        this.validationInterval = null;
      }
      
      console.log('All tokens cleared successfully');
    } catch (error) {
      console.error('Failed to clear tokens:', error);
    }
  }

  // Setup automatic logout before token expires
  setupAutoLogout() {
    // Clear existing timer
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
    }
    
    const timeUntilExpiration = this.getTimeUntilExpiration();
    
    if (timeUntilExpiration > 0) {
      // Logout 5 minutes before expiration, or immediately if less than 5 minutes
      const logoutDelay = Math.max(0, (timeUntilExpiration - 300) * 1000);
      
      console.log(`Auto-logout scheduled in ${Math.round(logoutDelay / 1000)} seconds`);
      
      this.logoutTimer = setTimeout(() => {
        console.log('Auto-logout triggered due to token expiration');
        this.handleAutoLogout();
      }, logoutDelay);
    }
  }

  // Handle automatic logout
  handleAutoLogout() {
    // Clear tokens
    this.clearTokens();
    
    // Dispatch custom event for components to handle
    window.dispatchEvent(new CustomEvent('tokenExpired', {
      detail: { reason: 'expiration' }
    }));
    
    // Show notification if available
    if (window.showToast) {
      window.showToast('Your session has expired. Please login again.', 'warning');
    } else {
      // Fallback alert
      alert('Your session has expired. Please login again.');
    }
    
    // Redirect to login
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }

  // Setup periodic token validation
  setupTokenValidation() {
    // Check token validity every 30 seconds
    this.validationInterval = setInterval(() => {
      if (this.getAccessToken() === null) {
        // Token was cleared or expired, stop validation
        if (this.validationInterval) {
          clearInterval(this.validationInterval);
          this.validationInterval = null;
        }
      }
    }, 30000);
  }

  // Check if user has valid authentication
  isAuthenticated() {
    const token = this.getAccessToken();
    return !!token && !this.isTokenExpired();
  }

  // Get token info for debugging
  getTokenInfo() {
    return {
      hasAccessToken: !!sessionStorage.getItem(this.TOKEN_KEY),
      hasRefreshToken: !!sessionStorage.getItem(this.REFRESH_TOKEN_KEY),
      expiresAt: sessionStorage.getItem(this.EXPIRES_AT_KEY),
      isExpired: this.isTokenExpired(),
      timeUntilExpiration: this.getTimeUntilExpiration(),
      isAuthenticated: this.isAuthenticated()
    };
  }
}

// Create and export singleton instance
const tokenManager = new TokenManager();

// Listen for storage changes (for multi-tab sync)
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === tokenManager.TOKEN_KEY || e.key === tokenManager.EXPIRES_AT_KEY) {
      console.log('Token storage changed in another tab');
      
      // If token was removed in another tab, clear local state
      if (!e.newValue && e.oldValue) {
        tokenManager.clearTokens();
        window.dispatchEvent(new CustomEvent('tokenExpired', {
          detail: { reason: 'removed_in_other_tab' }
        }));
      }
    }
  });
  
  // Handle tab/window close - cleanup
  window.addEventListener('beforeunload', () => {
    if (tokenManager.logoutTimer) {
      clearTimeout(tokenManager.logoutTimer);
    }
    if (tokenManager.validationInterval) {
      clearInterval(tokenManager.validationInterval);
    }
  });
}

export default tokenManager;